import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/AuthContext';
import {
  Box, Paper, Typography, TextField, Button, Alert, Stack,
  FormControlLabel, Checkbox, IconButton, InputAdornment,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const REMEMBERED_USERNAME_KEY = 'rememberedUsername';

export default function LoginPage() {
  const [isRegister, setIsRegister] = useState(false);
  const [form, setForm] = useState({
    username: localStorage.getItem(REMEMBERED_USERNAME_KEY) || '',
    password: '',
    email: '',
    fullName: '',
  });
  const [rememberMe, setRememberMe] = useState(
    !!localStorage.getItem(REMEMBERED_USERNAME_KEY)
  );
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { loginUser, registerUser } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isRegister) {
        await registerUser(form);
      } else {
        await loginUser({
          username: form.username,
          password: form.password,
          rememberMe,
        });
        if (rememberMe) {
          localStorage.setItem(REMEMBERED_USERNAME_KEY, form.username);
        } else {
          localStorage.removeItem(REMEMBERED_USERNAME_KEY);
        }
      }
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)' }}>
      <Paper sx={{ p: { xs: 3, sm: 5 }, width: '100%', maxWidth: 420, mx: 2, borderRadius: 3, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', border: 'none' }}>
        <Typography variant="h5" align="center" color="primary" gutterBottom>Azotte-MCRM</Typography>
        <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 4 }}>
          {isRegister ? 'Create your account' : 'Sign in to your account'}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField label="Username" name="username" value={form.username} onChange={handleChange} required autoComplete="username" />

            {isRegister && (
              <>
                <TextField label="Email" type="email" name="email" value={form.email} onChange={handleChange} required autoComplete="email" />
                <TextField label="Full Name" name="fullName" value={form.fullName} onChange={handleChange} autoComplete="name" />
              </>
            )}

            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={form.password}
              onChange={handleChange}
              required
              slotProps={{
                htmlInput: { minLength: isRegister ? 8 : undefined },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        onClick={() => setShowPassword(s => !s)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                        tabIndex={-1}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
              autoComplete={isRegister ? 'new-password' : 'current-password'}
            />

            {!isRegister && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                }
                label="Remember me"
              />
            )}

            <Button type="submit" variant="contained" fullWidth size="large" disabled={loading}>
              {loading ? 'Please wait...' : isRegister ? 'Create Account' : 'Sign In'}
            </Button>
          </Stack>
        </form>

      </Paper>
    </Box>
  );
}
